import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, Trash2, Edit2 } from "react-feather";
import { useTheme } from "../theme/ThemeContext";
import { getBillDisplayStatus, paidFromName } from "../utils/bills";
import { sourceRequired, paidFromFields, paidFromValue } from "../utils/moneySources";
import MoneySourceSelect from "../components/MoneySourceSelect";
import { useMoneySources } from "../components/useMoneySources";
import { paidBody } from "./BillsAndPayments";
import { BILL_FREQUENCIES, scheduleLabel, frequencyOf } from "../utils/billFrequency";

import { ALL_EXPENSE_TYPES as BILL_TYPES } from "../utils/expenseTypes";

import { API_BASE, apiFetch } from "../api";

const formatDate = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

const BillDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tokens, fonts } = useTheme();

  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editError, setEditError] = useState(null);
  const [saving, setSaving] = useState(false);
  const sources = useMoneySources();

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    apiFetch(`${API_BASE}/bills/${id}`)
      .then((res) => {
        if (!res.ok) throw new Error(res.status === 404 ? "Bill not found." : "Failed to load bill.");
        return res.json();
      })
      .then(setBill)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const startEdit = () => {
    const { repeatUnit, repeatEvery } = frequencyOf(bill);
    setEditForm({
      payee: bill.payee,
      type: bill.type,
      schedule: bill.schedule,
      dueDate: bill.dueDate,
      amount: String(bill.amount),
      autopay: !!bill.autopay,
      description: bill.description || "",
      repeatUnit,
      repeatEvery: String(repeatEvery),
      paidFrom: paidFromValue(bill),
      variableAmount: !!bill.variableAmount,
    });
    setEditError(null);
    setIsEditing(true);
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    setEditError(null);

    const amount = parseFloat(editForm.amount);
    if (!editForm.payee.trim()) return setEditError("Payee is required.");
    if (!editForm.dueDate) return setEditError("Due date is required.");
    if (Number.isNaN(amount) || amount < 0) return setEditError("Amount must be a valid number.");
    if (!editForm.paidFrom && sourceRequired(editForm.dueDate, sources.assets, sources.cards)) return setEditError("Choose where it's paid from.");

    setSaving(true);
    const repeatEvery = editForm.repeatUnit === "days" ? parseInt(editForm.repeatEvery, 10) || 1 : 1;
    const { paidFrom, ...fields } = editForm;
    apiFetch(`${API_BASE}/bills/${bill.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...fields, ...paidFromFields(paidFrom), amount, repeatEvery, schedule: scheduleLabel(editForm.repeatUnit, repeatEvery) }),
    })
      .then((res) => {
        if (!res.ok) return res.json().then((data) => Promise.reject(new Error(data.error)));
        return res.json();
      })
      .then((updated) => {
        setBill(updated);
        setIsEditing(false);
      })
      .catch((err) => setEditError(err.message || "Failed to save changes."))
      .finally(() => setSaving(false));
  };

  const handleToggleStatus = () => {
    const nextStatus = bill.status === "Paid" ? "Pending" : "Paid";
    // One whose amount changes each time asks what was actually paid.
    const body = nextStatus === "Paid" ? paidBody(bill) : { status: nextStatus };
    if (!body) return;
    setBill((prev) => ({ ...prev, ...body }));
    apiFetch(`${API_BASE}/bills/${bill.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).catch(() => load());
  };

  const handleDelete = () => {
    apiFetch(`${API_BASE}/bills/${bill.id}`, { method: "DELETE" })
      .then(() => navigate("/bills-and-payments"))
      .catch(() => load());
  };

  const handleViewHistory = () => {
    navigate(`/transactions?label=${encodeURIComponent(bill.payee)}`);
  };

  return (
    <div style={{ ...styles.page, backgroundColor: tokens.bg, fontFamily: fonts.body }}>
      <button onClick={() => navigate("/bills-and-payments")} style={{ ...styles.backBtn, color: tokens.textMuted }}>
        <ArrowLeft size={16} /> Back to Bills &amp; Payments
      </button>

      {loading && <p style={{ color: tokens.textMuted, fontSize: 14 }}>Loading...</p>}
      {error && (
        <div style={{ ...styles.banner, backgroundColor: tokens.pendingBg, color: tokens.pending }}>{error}</div>
      )}

      {!loading && !error && bill && !isEditing && (
        <div style={{ ...styles.card, backgroundColor: tokens.surface, border: `1px solid ${tokens.surfaceBorder}`, boxShadow: tokens.cardShadow }}>
          <div style={styles.headerRow}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <h2 style={{ ...styles.title, fontFamily: fonts.display, color: tokens.textPrimary }}>{bill.payee}</h2>
                {!!bill.autopay && (
                  <span style={{ ...styles.badge, backgroundColor: tokens.accentSoft, color: tokens.accent }}>AUTOPAY</span>
                )}
              </div>
              <span style={{ ...styles.typeTag, backgroundColor: tokens.neutralSoft, color: tokens.textMuted }}>
                {bill.type}
              </span>
            </div>
            <button
              onClick={handleToggleStatus}
              style={{
                ...styles.statusBtn,
                backgroundColor:
                  getBillDisplayStatus(bill) === "Paid" ? tokens.positiveBg
                  : getBillDisplayStatus(bill) !== "Upcoming" ? tokens.pendingBg
                  : tokens.neutralSoft,
                color:
                  getBillDisplayStatus(bill) === "Paid" ? tokens.positive
                  : getBillDisplayStatus(bill) !== "Upcoming" ? tokens.pending
                  : tokens.textMuted,
              }}
            >
              {getBillDisplayStatus(bill)}
            </button>
          </div>

          <div style={styles.grid}>
            <div style={styles.field}>
              <span style={{ ...styles.fieldLabel, color: tokens.textMuted }}>Amount</span>
              <span style={{ ...styles.fieldValue, fontFamily: fonts.mono, color: tokens.textPrimary }}>
                {bill.variableAmount && bill.status !== "Paid" ? "~" : ""}₹{bill.amount.toLocaleString()}
              </span>
            </div>
            <div style={styles.field}>
              <span style={{ ...styles.fieldLabel, color: tokens.textMuted }}>Due Date</span>
              <span style={{ ...styles.fieldValue, color: tokens.textPrimary }}>{formatDate(bill.dueDate)}</span>
            </div>
            <div style={styles.field}>
              <span style={{ ...styles.fieldLabel, color: tokens.textMuted }}>Schedule</span>
              <span style={{ ...styles.fieldValue, color: tokens.textPrimary }}>{bill.schedule}</span>
            </div>
            <div style={styles.field}>
              <span style={{ ...styles.fieldLabel, color: tokens.textMuted }}>Payment Method</span>
              <span style={{ ...styles.fieldValue, color: tokens.textPrimary }}>
                {bill.autopay ? "Autopay" : "Manual"}
              </span>
            </div>
            <div style={styles.field}>
              <span style={{ ...styles.fieldLabel, color: tokens.textMuted }}>Paid from</span>
              <span style={{ ...styles.fieldValue, color: tokens.textPrimary }}>{bill.paidFromKind ? paidFromName(bill, sources) : "Not set"}</span>
            </div>
          </div>

          {!!bill.description && (
            <div style={styles.descriptionBox}>
              <span style={{ ...styles.fieldLabel, color: tokens.textMuted }}>Description</span>
              <p style={{ margin: "4px 0 0", fontSize: 14, color: tokens.textPrimary }}>{bill.description}</p>
            </div>
          )}

          <div style={styles.actions}>
            <button onClick={startEdit} style={{ ...styles.editBtn, borderColor: tokens.surfaceBorder, color: tokens.textPrimary }}>
              <Edit2 size={15} /> Edit
            </button>
            <button onClick={handleViewHistory} style={{ ...styles.historyBtn, backgroundColor: tokens.accent, color: tokens.accentContrast }}>
              <Clock size={15} /> View History
            </button>
            <button onClick={handleDelete} style={{ ...styles.deleteBtn, color: tokens.negative, borderColor: tokens.surfaceBorder }}>
              <Trash2 size={15} /> Delete Bill
            </button>
          </div>
        </div>
      )}

      {!loading && !error && bill && isEditing && (
        <form
          onSubmit={handleEditSubmit}
          style={{ ...styles.card, backgroundColor: tokens.surface, border: `1px solid ${tokens.surfaceBorder}`, boxShadow: tokens.cardShadow }}
        >
          <h2 style={{ ...styles.title, fontFamily: fonts.display, color: tokens.textPrimary, marginBottom: 20 }}>
            Edit Bill
          </h2>

          <div style={styles.editGrid}>
            <label style={styles.editField}>
              <span style={{ ...styles.fieldLabel, color: tokens.textMuted }}>Payee</span>
              <input
                value={editForm.payee}
                onChange={(e) => setEditForm({ ...editForm, payee: e.target.value })}
                style={{ ...styles.editInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.bg }}
              />
            </label>
            <label style={styles.editField}>
              <span style={{ ...styles.fieldLabel, color: tokens.textMuted }}>Type</span>
              <select
                value={editForm.type}
                onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                style={{ ...styles.editInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.bg }}
              >
                {BILL_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </label>
            <label style={styles.editField}>
              <span style={{ ...styles.fieldLabel, color: tokens.textMuted }}>Frequency</span>
              <select
                value={editForm.repeatUnit}
                onChange={(e) => setEditForm({ ...editForm, repeatUnit: e.target.value })}
                style={{ ...styles.editInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.bg }}
              >
                {BILL_FREQUENCIES.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </label>
            {editForm.repeatUnit === "days" && (
              <label style={styles.editField}>
                <span style={{ ...styles.fieldLabel, color: tokens.textMuted }}>Every how many days?</span>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={editForm.repeatEvery}
                  onChange={(e) => setEditForm({ ...editForm, repeatEvery: e.target.value })}
                  style={{ ...styles.editInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.bg }}
                />
              </label>
            )}
            <label style={styles.editField}>
              <span style={{ ...styles.fieldLabel, color: tokens.textMuted }}>Due Date</span>
              <input
                type="date"
                value={editForm.dueDate}
                onChange={(e) => setEditForm({ ...editForm, dueDate: e.target.value })}
                style={{ ...styles.editInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.bg }}
              />
            </label>
            <label style={styles.editField}>
              <span style={{ ...styles.fieldLabel, color: tokens.textMuted }}>Amount</span>
              <input
                type="number"
                step="0.01"
                value={editForm.amount}
                onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                style={{ ...styles.editInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.bg }}
              />
            </label>
            <label style={{ ...styles.editField, flexDirection: "row", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                checked={editForm.autopay}
                onChange={(e) => setEditForm({ ...editForm, autopay: e.target.checked })}
              />
              <span style={{ fontSize: 14, color: tokens.textPrimary }}>Autopay</span>
            </label>
            {editForm.repeatUnit !== "once" && (
              <label style={{ ...styles.editField, flexDirection: "row", alignItems: "center", gap: 8 }}>
                <input type="checkbox" checked={editForm.variableAmount} onChange={(e) => setEditForm({ ...editForm, variableAmount: e.target.checked })} />
                <span style={{ fontSize: 14, color: tokens.textPrimary }}>Amount changes each time (the amount is an estimate)</span>
              </label>
            )}
            <label style={styles.editField}>
              <span style={{ ...styles.fieldLabel, color: tokens.textMuted }}>Paid from</span>
              <MoneySourceSelect
                style={{ ...styles.editInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.bg }}
                value={editForm.paidFrom}
                onChange={(v) => setEditForm({ ...editForm, paidFrom: v })}
                assets={sources.assets}
                cards={sources.cards}
                required={sourceRequired(editForm.dueDate, sources.assets, sources.cards)}
              />
            </label>
          </div>

          <label style={{ ...styles.editField, marginBottom: 20 }}>
            <span style={{ ...styles.fieldLabel, color: tokens.textMuted }}>Description</span>
            <input
              value={editForm.description}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              style={{ ...styles.editInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.bg }}
            />
          </label>

          <div style={styles.actions}>
            <button type="submit" disabled={saving} style={{ ...styles.historyBtn, backgroundColor: tokens.accent, color: tokens.accentContrast }}>
              {saving ? "Saving..." : "Save Changes"}
            </button>
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              style={{ ...styles.editBtn, borderColor: tokens.surfaceBorder, color: tokens.textMuted }}
            >
              Cancel
            </button>
            {editError && <span style={{ fontSize: 13, fontWeight: 600, color: tokens.negative }}>{editError}</span>}
          </div>
        </form>
      )}
    </div>
  );
};

const styles = {
  page: {
    padding: "24px",
    minHeight: "100vh",
    transition: "background-color 0.2s ease",
  },
  backBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    background: "transparent",
    border: "none",
    cursor: "pointer",
    fontSize: 13.5,
    fontWeight: 600,
    marginBottom: 20,
    padding: 0,
  },
  banner: {
    padding: "12px 16px",
    borderRadius: "10px",
    fontSize: "13.5px",
    fontWeight: 600,
  },
  card: {
    padding: "28px",
    borderRadius: "18px",
    maxWidth: 640,
    transition: "background-color 0.2s ease, border-color 0.2s ease",
  },
  headerRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
    marginBottom: 24,
    paddingBottom: 20,
    borderBottom: "1px solid rgba(128,128,128,0.2)",
  },
  title: {
    margin: 0,
    fontSize: 24,
    fontWeight: 700,
  },
  badge: {
    fontSize: 11,
    fontWeight: 700,
    padding: "3px 9px",
    borderRadius: 999,
  },
  typeTag: {
    display: "inline-block",
    marginTop: 8,
    fontSize: 12,
    fontWeight: 600,
    padding: "3px 10px",
    borderRadius: 999,
  },
  statusBtn: {
    border: "none",
    borderRadius: 999,
    padding: "8px 18px",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: 20,
    marginBottom: 20,
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
  },
  fieldValue: {
    fontSize: 16,
    fontWeight: 700,
  },
  descriptionBox: {
    marginBottom: 24,
    paddingTop: 16,
    borderTop: "1px solid rgba(128,128,128,0.2)",
  },
  actions: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
  },
  historyBtn: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    border: "none",
    borderRadius: 10,
    padding: "11px 20px",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
  },
  deleteBtn: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "transparent",
    border: "1px solid",
    borderRadius: 10,
    padding: "11px 20px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
  editBtn: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "transparent",
    border: "1px solid",
    borderRadius: 10,
    padding: "11px 20px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
  editGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 16,
    marginBottom: 16,
  },
  editField: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  editInput: {
    padding: "9px 12px",
    borderRadius: 8,
    border: "1px solid",
    fontSize: 14,
  },
};

export default BillDetail;
